import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { logger, sameWidth } from 'shared/lib'
import {
  Box,
  Dropdown,
  EnumDropdownItemVariant,
  Error,
  type IDropdownItem,
  Label,
  Typography,
} from 'shared/ui'
import { ChipWithAvatar } from 'entities/Phone/ui/AssignNumberCommon/ui/ChipWithAvatar/ChipWithAvatar'
import { MultiSelectDropdownContent } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/MultiSelectDropdownContent'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { userToDropdownItem } from 'entities/Users/lib/userToDropdownItem'
import { type UsersBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/UsersBlock'
import { RenderItemsContent } from './RenderItemsContent/RenderItemsContent'
import styles from './styles.module.scss'

const AUTO_10_ITEM = {
  id: 'auto_10_members',
  label: 'Auto · 10 inbox members',
  iconL: 'users' as const,
  variant: EnumDropdownItemVariant.CheckedRight,
}

export const UsersBlockEditContent = observer(
  ({ store, inboxMemberIds }: { store: UsersBlockStore; inboxMemberIds: number[] }) => {
    const { usersStore } = useEntitiesContext()

    const {
      setSelectedMembers,
      deleteSelectedMembers,
      selectedMembers,
      error,
      setError,
      initUsersIds,
      setInitMembers,
      isAutoTenMembers,
      setAutoTenMembers,
      clearAutoTenMembers,
    } = store

    useEffect(() => {
      if (initUsersIds) {
        usersStore.initMembersPromise.then(async () => {
          try {
            const items = await usersStore.getItemsByIds(initUsersIds)
            setInitMembers(items.map(userToDropdownItem))
          } catch (e) {
            logger.error(e)
          }
        })
      }
    }, [initUsersIds])

    const selectedMembersItems = useMemo(() => {
      return selectedMembers.map((item) => ({
        id: item.id,
        label: item.label,
        value: item.value,
        data: {
          isAway: undefined,
          avatarInfo: item.avatarProps?.info,
          email: item.data?.email,
        },
      }))
    }, [selectedMembers])

    const displayItems = useMemo(() => {
      if (isAutoTenMembers) {
        return [{ id: AUTO_10_ITEM.id, label: AUTO_10_ITEM.label }]
      }
      return selectedMembersItems
    }, [isAutoTenMembers, selectedMembersItems])

    const handleDelete = (item: IDropdownItem) => {
      if (item.id === AUTO_10_ITEM.id) {
        clearAutoTenMembers()
      } else {
        deleteSelectedMembers(item)
      }
    }

    const inboxMemberIdsSet = useMemo(() => new Set(inboxMemberIds), [inboxMemberIds])

    const allItems = useMemo(() => {
      const memberItems = inboxMemberIds.length
        ? usersStore.membersDropdownItems.filter((item) => inboxMemberIdsSet.has(Number(item.id)))
        : usersStore.membersDropdownItems
      return [{ ...AUTO_10_ITEM, activeValue: isAutoTenMembers || undefined }, ...memberItems]
    }, [
      isAutoTenMembers,
      usersStore.membersDropdownItems,
      inboxMemberIdsSet,
      inboxMemberIds.length,
    ])

    return (
      <Box display={'flex'} flexDirection={'column'} gap={0.5}>
        <Label
          label={{
            text: 'Users',
          }}
        />
        <Dropdown
          width={'100%'}
          triggerComponent={() => (
            <TextFieldPlaceholder
              size={'medium'}
              variant={'stroke'}
              className={styles.multiSelect}
              placeholderClassName={styles.placeholder}
              onClick={() => setError('')}
              error={error}
              placeholder={
                <>
                  {displayItems.length ? (
                    <RenderItemsContent items={displayItems} onDelete={handleDelete} />
                  ) : (
                    <Typography
                      ariaLabel={'select'}
                      variant={'text-input-field-r'}
                      color={'var(--content-neutral-primary)'}
                      className={styles.empty}
                    >
                      Select users
                    </Typography>
                  )}
                </>
              }
              rightActions={[
                makeTextFieldIcon({
                  icon: 'chevronDown',
                }),
              ]}
            />
          )}
          customComponent={() => (
            <MultiSelectDropdownContent
              ChipComponent={ChipWithAvatar}
              items={allItems}
              selectedItems={displayItems}
              onSelect={(item) => {
                if (item.id === AUTO_10_ITEM.id) {
                  setAutoTenMembers()
                } else {
                  setSelectedMembers(item)
                }
              }}
              onDelete={handleDelete}
            />
          )}
          PopperProps={{
            modifiers: [
              sameWidth(),
              {
                name: 'offset',
                options: {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  offset: ({ reference }) => {
                    return [0, -reference.height - 8]
                  },
                },
              },
            ],
          }}
        />
        {error && <Error type={'text'} error={error} />}
        {!error && store.isLimitReached && store.maxMembers !== null && (
          <Error
            type={'text'}
            error={`You can add up to ${store.maxMembers} members to a simultaneous ring. Remove a member to continue.`}
          />
        )}
      </Box>
    )
  }
)
